import React from 'react';
import { EDSButton } from '@gehc-eds-react/eds-button';
import { EDSIcon } from '@gehc-eds-react/eds-icon';
import { EDSSearch } from '@gehc-eds-react/eds-search';
 
const TestConnectionIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path d="M9 7V3M15 7V3M8 7h8v4a4 4 0 0 1-4 4 4 4 0 0 1-4-4V7z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 15v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);
 
// Small glyph shown inside each status pill, matched to its meaning rather than a plain dot.
const StatusIcon = ({ kind, color }) => {
    if (kind === 'connected') {
        return (
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                <path d="M5 13l4 4L19 7" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        );
    }
    if (kind === 'failed') {
        return (
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                <path d="M6 6l12 12M18 6L6 18" stroke={color} strokeWidth="3" strokeLinecap="round" />
            </svg>
        );
    }
    if (kind === 'partial') {
        return (
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                <path d="M12 2 1 21h22L12 2z" stroke={color} strokeWidth="2" strokeLinejoin="round" />
                <path d="M12 9v5M12 17h.01" stroke={color} strokeWidth="2" strokeLinecap="round" />
            </svg>
        );
    }
    return <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />;
};
 
// Maps the source's `connection_status` (from the last test-connection run) to a
// small status pill. Sources that have never been tested show a neutral "Not tested".
const getConnectionStatusTone = (status) => {
    const key = (status || '').toUpperCase();
 
    if (!key) {
        return { label: 'Not tested', color: '#4F63D2', backgroundColor: '#EEF1FE', icon: 'idle' };
    }
    if (['PASSED', 'PASS', 'CONNECTED', 'HEALTHY', 'SUCCESS'].includes(key)) {
        return { label: 'Connected', color: '#139c48', backgroundColor: '#e8f5e9', icon: 'connected' };
    }
    if (['FAILED', 'FAIL', 'ERROR'].includes(key)) {
        return { label: 'Failed', color: '#c62828', backgroundColor: '#ffebee', icon: 'failed' };
    }
    if (key.includes('PARTIAL')) {
        return { label: 'Partial', color: '#b7791f', backgroundColor: '#fdf3e1', icon: 'partial' };
    }
    // Fallback: show the raw status, title-cased, so an unmapped value is still readable.
    const titleCased = key.charAt(0) + key.slice(1).toLowerCase().replace(/_/g, ' ');
    return { label: titleCased, color: '#8a8f98', backgroundColor: '#f3f4f6', icon: 'idle' };
};
 
// Turns an ISO timestamp into a short relative label ("2h ago", "3d ago") for the
// "last tested" hint next to the status pill.
const formatRelativeTime = (isoString) => {
    if (!isoString) return '';
    const then = new Date(isoString).getTime();
    if (Number.isNaN(then)) return '';
 
    const diffMs = Date.now() - then;
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
};
 
const headerCellStyle = {
    textAlign: 'left',
    borderBottom: '2px solid #6D55B6',
    padding: '13px 8px',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: '#5B4A87',
    whiteSpace: 'nowrap'
};
 
const SortableHeader = ({ label, sortKey, onSort, getSortIndicator }) => (
    <th style={headerCellStyle}>
        <button
            type="button"
            onClick={() => onSort(sortKey)}
            style={{ background: 'none', border: 'none', padding: 0, margin: 0, cursor: 'pointer', font: 'inherit', color: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit' }}
        >
            {label}{getSortIndicator(sortKey)}
        </button>
    </th>
);
 
const IconButton = ({ onClick, title, ariaLabel, variant, children }) => (
    <button
        type="button"
        onClick={onClick}
        title={title}
        aria-label={ariaLabel}
        className={`s3-icon-btn s3-icon-btn--${variant}`}
        style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px',
            padding: 0,
            borderRadius: '8px',
            border: '1px solid transparent',
            background: 'transparent',
            cursor: 'pointer',
            transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease'
        }}
    >
        {children}
    </button>
);
 
/**
* Right-panel source list: search box, sortable table, per-row Deboard /
* Test Connection actions (including navigating to source details), and a
* pagination footer.
*
* All data (accounts, sort/search state, pagination) and mutations are owned
* by the parent (CreateS3Upload); this component only renders them and calls
* back out on interaction — including `onOpenSourceDetails` (the row's
* clickable Account ID / Display Name link), which the parent implements as
* `handleOpenSourceDetails`.
*/
const SourceListTable = ({
    selectedContractId,
    sourceSearchTerm,
    onSourceSearchInput,
    onSourceSearchClear,
    isAccountsLoading,
    accountsError,
    sourceAccounts,
    filteredSourceAccounts,
    onSort,
    getSortIndicator,
    scrollRef,
    firstMatchedRowRef,
    onOpenSourceDetails,
    onDeleteAccount,
    onTestConnection,
    isTestConnectionRunning,
    accountsPagination,
    onPreviousPage,
    onNextPage
}) => (
    <div
        className="u--3/4 fill--1"
        style={{
            padding: '12px 24px 0 24px',
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
        }}
    >
        <style>{`
            .s3-source-row { transition: background-color 0.12s ease; }
            .s3-source-row:nth-child(even) { background-color: #fbfaff; }
            .s3-source-row:hover { background-color: #f4f1fb; }
            .s3-icon-btn--delete:hover { background-color: #FBEAEA; border-color: #F5D6D6; color: #C62828; }
            .s3-test-conn-btn:hover:not(:disabled) { background: linear-gradient(135deg, #5B45A0 0%, #4A3986 100%); transform: translateY(-1px); box-shadow: 0 4px 10px rgba(109, 85, 182, 0.35); }
            .s3-test-conn-btn:active:not(:disabled) { transform: translateY(0); }
            .s3-account-link:hover .s3-account-arrow { transform: translateX(2px); opacity: 1; }
        `}</style>
 
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flex: '0 0 auto', minHeight: 0 }}>
            <h4 className="u-mt0 u-mb--">Source List</h4>
            <div style={{ width: '320px', maxWidth: '100%', flexShrink: 0 }}>
                <EDSSearch
                    useSearchButton={false}
                    variant="type-in"
                    addListOption="beginning"
                    searchTerm={sourceSearchTerm}
                    placeholder="Search sources..."
                    onEDSSearchInput={onSourceSearchInput}
                    onEDSSearchClear={onSourceSearchClear}
                />
            </div>
        </div>
 
        {!selectedContractId && (
            <div style={{ marginTop: '16px', color: '#666' }}>
                No contract selected.
            </div>
        )}
 
        {selectedContractId && (
            <>
                {isAccountsLoading && (
                    <div style={{ marginTop: '16px' }}>Loading source accounts...</div>
                )}
 
                {!isAccountsLoading && accountsError && (
                    <div style={{ marginTop: '16px', color: '#d32f2f' }}>{accountsError}</div>
                )}
 
                {!isAccountsLoading && !accountsError && (
                    <div
                        ref={scrollRef}
                        style={{
                            marginTop: '12px',
                            flex: '1 1 auto',
                            minHeight: 0,
                            overflow: 'auto',
                            border: '1px solid #e8e6f2',
                            borderRadius: '12px',
                            boxShadow: '0 1px 3px rgba(109, 85, 182, 0.08)'
                        }}
                    >
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', tableLayout: 'fixed' }}>
                            <colgroup>
                                <col style={{ width: '24%' }} />
                                <col style={{ width: '15%' }} />
                                <col style={{ width: '12%' }} />
                                <col style={{ width: '14%' }} />
                                <col style={{ width: '7%' }} />
                                <col style={{ width: '28%' }} />
                            </colgroup>
                            <thead>
                                <tr style={{ backgroundColor: '#fafbfc' }}>
                                    <SortableHeader label="Account ID - Display Name" sortKey="account_display_name" onSort={onSort} getSortIndicator={getSortIndicator} />
                                    <SortableHeader label="Source Bucket" sortKey="source_bucket" onSort={onSort} getSortIndicator={getSortIndicator} />
                                    <SortableHeader label="Replicate Prefix" sortKey="replicate_prefix" onSort={onSort} getSortIndicator={getSortIndicator} />
                                    <th style={{ ...headerCellStyle, textAlign: 'center' }}>Status</th>
                                    <th style={{ ...headerCellStyle, textAlign: 'center' }}>Deboard</th>
                                    <th style={headerCellStyle}>Test Connection</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSourceAccounts.length === 0 && sourceAccounts.length === 0 && (
                                    <tr>
                                        <td colSpan={6} style={{ padding: '12px 8px', color: '#666' }}>
                                            No active source accounts found for this contract.
                                        </td>
                                    </tr>
                                )}
 
                                {filteredSourceAccounts.length === 0 && sourceAccounts.length > 0 && sourceSearchTerm.trim() && (
                                    <tr>
                                        <td colSpan={6} style={{ padding: '12px 8px', color: '#666' }}>
                                            No source accounts match "{sourceSearchTerm.trim()}".
                                        </td>
                                    </tr>
                                )}
 
                                {filteredSourceAccounts.map((account, index) => (
                                    <tr
                                        key={`${account.account_id}-${account.source_bucket}`}
                                        ref={index === 0 ? firstMatchedRowRef : null}
                                        className="s3-source-row"
                                    >
                                        <td style={{ borderBottom: '1px solid #f1f1f1', padding: '14px 8px', verticalAlign: 'middle' }}>
                                            <button
                                                type="button"
                                                onClick={() => onOpenSourceDetails(account)}
                                                className="s3-account-link"
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: 0,
                                                    margin: 0,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    color: '#0057b8',
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                    textAlign: 'left'
                                                }}
                                                title="Open source details"
                                                aria-label={`Open source details for ${account.account_id || 'source'}`}
                                            >
                                                <span style={{ textDecoration: 'underline' }}>
                                                    {account.account_id || '--'}{account.display_name ? ` - ${account.display_name}` : ''}
                                                </span>
                                                <span className="s3-account-arrow" style={{ opacity: 0.5, transition: 'transform 0.15s ease, opacity 0.15s ease' }}>›</span>
                                            </button>
                                        </td>
                                        <td style={{ borderBottom: '1px solid #f1f1f1', padding: '14px 8px', verticalAlign: 'middle', fontFamily: "'JetBrains Mono', ui-monospace, Menlo, monospace", fontSize: '12.5px', color: '#4b5563' }}>{account.source_bucket || '--'}</td>
                                        <td style={{ borderBottom: '1px solid #f1f1f1', padding: '14px 8px', verticalAlign: 'middle', fontFamily: "'JetBrains Mono', ui-monospace, Menlo, monospace", fontSize: '12.5px', color: '#4b5563' }}>{account.replicate_prefix || '--'}</td>
                                        <td style={{ borderBottom: '1px solid #f1f1f1', padding: '14px 8px', verticalAlign: 'middle', textAlign: 'center' }}>
                                            {(() => {
                                                const tone = getConnectionStatusTone(account.connection_status);
                                                const relativeTime = formatRelativeTime(account.last_tested_at);
                                                return (
                                                    <span
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '6px',
                                                            color: tone.color,
                                                            backgroundColor: tone.backgroundColor,
                                                            border: `1px solid ${tone.color}33`,
                                                            fontSize: '11.5px',
                                                            fontWeight: 700,
                                                            lineHeight: 1,
                                                            padding: '4px 10px',
                                                            borderRadius: '999px',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        <StatusIcon kind={tone.icon} color={tone.color} />
                                                        {tone.label}
                                                        {relativeTime && (
                                                            <>
                                                                <span style={{ width: '1px', height: '11px', backgroundColor: `${tone.color}33`, flexShrink: 0 }} />
                                                                <span style={{ fontWeight: 500, color: `${tone.color}b3` }}>
                                                                    {relativeTime}
                                                                </span>
                                                            </>
                                                        )}
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                        <td style={{ borderBottom: '1px solid #f1f1f1', padding: '14px 8px', verticalAlign: 'middle', textAlign: 'center' }}>
                                            <IconButton
                                                onClick={() => onDeleteAccount(account)}
                                                title="Deboard account"
                                                ariaLabel={`Deboard account ${account.account_id || ''}`.trim()}
                                                variant="delete"
                                            >
                                                <EDSIcon iconName="ico-trash-16" />
                                            </IconButton>
                                        </td>
                                        <td style={{ borderBottom: '1px solid #f1f1f1', padding: '14px 8px', verticalAlign: 'middle' }}>
                                            <button
                                                type="button"
                                                onClick={() => onTestConnection(account)}
                                                disabled={isTestConnectionRunning}
                                                title="Test connection"
                                                aria-label={`Test connection ${account.account_id || ''}`.trim()}
                                                className="s3-test-conn-btn"
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '6px',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    padding: '6px 14px',
                                                    fontSize: '13px',
                                                    fontWeight: 700,
                                                    lineHeight: 1,
                                                    color: '#fff',
                                                    background: 'linear-gradient(135deg, #6D55B6 0%, #5B45A0 100%)',
                                                    boxShadow: '0 1px 2px rgba(91, 69, 160, 0.3)',
                                                    cursor: isTestConnectionRunning ? 'default' : 'pointer',
                                                    opacity: isTestConnectionRunning ? 0.6 : 1,
                                                    transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                <TestConnectionIcon />
                                                Test connection
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </>
        )}
 
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center', marginTop: '4px', paddingBottom: '0', gap: '12px', flex: '0 0 auto' }}>
            <div style={{ color: '#666', fontSize: '13px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                Page {accountsPagination.page} of {Math.max(accountsPagination.totalPages, 1)} | Total accounts: {accountsPagination.totalCount}
            </div>
            <div style={{ display: 'grid', gridAutoFlow: 'column', gridAutoColumns: 'max-content', gap: '8px', justifySelf: 'end' }}>
                <EDSButton
                    variant="secondary"
                    type="button"
                    label="Previous"
                    onEDSButtonClick={onPreviousPage}
                    isDisabled={accountsPagination.page <= 1 || isAccountsLoading}
                />
                <EDSButton
                    variant="secondary"
                    type="button"
                    label="Next"
                    onEDSButtonClick={onNextPage}
                    isDisabled={
                        accountsPagination.totalPages === 0 ||
                        accountsPagination.page >= accountsPagination.totalPages ||
                        isAccountsLoading
                    }
                />
            </div>
        </div>
    </div>
);
 
export default SourceListTable;
